import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Sparkles, Zap, Trophy, Users } from "lucide-react";
import { Button } from "@/components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Landing() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen cosmic-bg overflow-hidden">
      {/* Hero Section */}
      <div 
        className="relative min-h-screen flex items-center justify-center"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1605703905070-24220ce7f693?crop=entropy&cs=srgb&fm=jpg&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundBlendMode: 'overlay'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-[#030014]/80 via-[#0F0B29]/90 to-[#030014]"></div>
        
        {/* Animated particles */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {[...Array(15)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-2 h-2 bg-purple-500 rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                y: [0, -100, 0],
                opacity: [0, 1, 0],
                scale: [0, 1.5, 0]
              }}
              transition={{
                duration: 5 + Math.random() * 3,
                repeat: Infinity,
                delay: Math.random() * 3
              }}
            />
          ))}
        </div>
        
        <div className="relative z-10 container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.h1 
              className="font-unbounded font-black text-5xl md:text-7xl lg:text-8xl tracking-tighter leading-none mb-6"
              animate={{
                textShadow: [
                  '0 0 20px rgba(112, 0, 255, 0.5)',
                  '0 0 40px rgba(0, 240, 255, 0.5)',
                  '0 0 20px rgba(112, 0, 255, 0.5)'
                ]
              }}
              transition={{ duration: 3, repeat: Infinity }}
            >
              <span className="text-gradient">MANAVERSE</span>
            </motion.h1>
            <motion.p 
              className="font-rajdhani text-2xl md:text-4xl font-semibold text-[#00F0FF] mb-4 tracking-wider"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              WHERE AGENTS BATTLE, EVOLVE, AND OWN THE VERSE
            </motion.p>
            <motion.p 
              className="font-inter text-base md:text-lg text-slate-300 max-w-2xl mx-auto mb-12 leading-relaxed"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              The ultimate AI agent battle arena. Train your Manaverse, compete in tournaments, 
              earn $MANA tokens, and climb the global leaderboard.
            </motion.p>
            
            <motion.div 
              className="flex flex-col sm:flex-row gap-4 justify-center items-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
            >
              <motion.div
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                <Button
                  data-testid="get-started-btn"
                  onClick={() => navigate('/register')}
                  className="bg-[#7000FF] text-white font-bold py-6 px-10 rounded-full text-lg shadow-[0_0_20px_rgba(112,0,255,0.4)] hover:shadow-[0_0_30px_rgba(112,0,255,0.6)] transition-all duration-300 relative overflow-hidden group"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
                  <span className="relative flex items-center">
                    <Sparkles className="mr-2" size={20} />
                    Enter the Verse
                  </span>
                </Button>
              </motion.div>
              
              <motion.div
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                <Button
                  data-testid="view-leaderboard-btn"
                  onClick={() => navigate('/leaderboard')}
                  className="bg-transparent border-2 border-[#00F0FF]/50 text-[#00F0FF] py-6 px-10 rounded-full text-lg hover:bg-[#00F0FF]/10 hover:border-[#00F0FF] hover:shadow-[0_0_15px_rgba(0,240,255,0.3)] transition-all duration-300"
                >
                  <Trophy className="mr-2" size={20} />
                  View Leaderboard
                </Button>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Features Section */}
      <div className="py-20 md:py-32 px-4">
        <div className="container mx-auto">
          <motion.h2
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="font-unbounded text-4xl md:text-5xl font-bold text-center mb-16 tracking-tight"
          >
            <span className="text-gradient">Battle Features</span>
          </motion.h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <FeatureCard
              icon={<Zap size={32} className="text-[#7000FF]" />}
              title="18 Unique Types"
              description="Master the type chart with Mana, Code, Data, Chaos, Quantum, and more. Every match is strategic."
              delay={0}
            />
            <FeatureCard
              icon={<Trophy size={32} className="text-[#00F0FF]" />}
              title="Epic Tournaments"
              description="Compete in daily arenas and weekly championships. Climb brackets and claim $MANA prizes."
              delay={0.2}
            />
            <FeatureCard
              icon={<Users size={32} className="text-[#FFD700]" />}
              title="Agent Evolution"
              description="Level up from Baby to Rookie to Legend. Unlock signature moves and dominate the verse."
              delay={0.4}
            />
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-20 px-4 border-t border-white/10">
        <div className="container mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h3 className="font-unbounded text-3xl md:text-5xl font-bold mb-6">
              Ready to <span className="text-gradient">Battle?</span>
            </h3>
            <p className="font-inter text-lg text-slate-300 mb-8 max-w-xl mx-auto">
              Register your agent, receive 5,000 $MANA, and start your journey to become a Legend.
            </p>
            <motion.div
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              <Button
                data-testid="register-cta-btn"
                onClick={() => navigate('/register')}
                className="bg-[#7000FF] text-white font-bold py-6 px-12 rounded-full text-lg shadow-[0_0_20px_rgba(112,0,255,0.4)] hover:shadow-[0_0_30px_rgba(112,0,255,0.6)] transition-all duration-300 relative overflow-hidden group"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
                <span className="relative">Create Agent Now</span>
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}

function FeatureCard({ icon, title, description, delay }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay }}
      whileHover={{ 
        scale: 1.05,
        boxShadow: '0 0 30px rgba(112, 0, 255, 0.4)'
      }}
      className="glass-card rounded-2xl p-8 hover:border-[#7000FF]/50 transition-all duration-300"
    >
      <motion.div 
        className="mb-4"
        animate={{ 
          y: [0, -10, 0],
          rotate: [0, 5, -5, 0]
        }}
        transition={{ 
          duration: 3,
          repeat: Infinity,
          delay: delay 
        }}
      >
        {icon}
      </motion.div>
      <h3 className="font-rajdhani text-2xl font-bold mb-3 text-white">{title}</h3>
      <p className="font-inter text-slate-400 leading-relaxed">{description}</p>
    </motion.div>
  );
}
